import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import client from "@/lib/api";
import { logger } from "@/lib/logger";
import Divider from "@/components/Divider";
import FlipThrough from "@/components/FlipThrough";
import PaystackCheckoutModal from "@/components/PaystackCheckoutModal";
import SamplerCaptureModal from "@/components/SamplerCaptureModal";
import WaitlistModal from "@/components/WaitlistModal";

const API_BASE = process.env.REACT_APP_BACKEND_URL;
const IMGS = {
  sampler: `${API_BASE}/api/images/brand/chapter-starters`,
  cookbook: `${API_BASE}/api/images/brand/cover`,
  bundle: `${API_BASE}/api/images/brand/chapter-sweet-close`,
};

const RIBBONS = {
  sampler: "Free",
  cookbook: "Most Popular",
  bundle: "Premium Experience",
};

const TAGLINES = {
  sampler: "A five-recipe introduction to the Mizizi world.",
  cookbook: "The complete digital cookbook.",
  bundle: "An expanded cooking and hosting experience.",
};

const FEATURES = {
  sampler: [
    "Five signature recipes as a digital sampler",
    "Immediate PDF delivery to your inbox",
    "Introduction to the Mizizi philosophy",
  ],
  cookbook: [
    "30 fully-developed recipes across 6 chapters",
    "3 complete menus with timelines & shopping lists",
    "Pantry guide, techniques & 5 foundational sauces",
    "Mobile-optimized reader + print-ready PDF",
    "Access to future corrections and updates to this edition",
  ],
  bundle: [
    "Everything in the complete cookbook",
    "Recorded Mizizi cooking session",
    "Premium high-resolution print-ready cookbook PDF",
    "Exclusive Mizizi Table hosting cards",
    "Curated menus and shopping-list bundle",
    "Early access to future Mizizi releases",
  ],
};

function detectDefaultCurrency() {
  try {
    const langs = navigator.languages || [navigator.language || ""];
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || "";
    if (langs.some((l) => /\-KE\b/i.test(l)) || /Nairobi/i.test(tz)) return "KES";
  } catch (err) {
    logger.warn("Currency locale detection failed:", err);
  }
  return "USD";
}

const fmtKes = (n) => `KES ${Number(n).toLocaleString("en-KE")}`;
const fmtUsd = (cents) => `$${(cents / 100).toFixed(0)}`;

function KesPrice({ product }) {
  return (
    <div className="mt-4">
      <div className="overline text-mizizi-terracotta">Founding Edition</div>
      <div className="font-serif text-4xl md:text-5xl leading-none mt-1" data-testid={`price-founding`}>
        {fmtKes(product.price_kes)}
      </div>
      {product.regular_price_kes && (
        <div className="mt-2 text-sm text-mizizi-charcoal/55">
          Regular price <span className="line-through decoration-mizizi-terracotta/70" data-testid="price-regular">{fmtKes(product.regular_price_kes)}</span>
        </div>
      )}
    </div>
  );
}

function UsdPrice({ product }) {
  if (!product.price) return <div className="mt-4 font-serif text-3xl">Free</div>;
  return <div className="mt-4 font-serif text-3xl" data-testid="price-usd">{fmtUsd(product.price)}</div>;
}

function Ribbon({ featured, children, testid }) {
  const colour = featured
    ? "bg-mizizi-terracotta text-mizizi-cream"
    : "bg-mizizi-gold text-mizizi-charcoal";
  return (
    <div
      data-testid={testid}
      className={`absolute -top-3 left-6 overline ${colour} px-3 py-1 z-10`}
    >
      {children}
    </div>
  );
}

export default function Shop() {
  const [products, setProducts] = useState({});
  const [email, setEmail] = useState("");
  const [loadingId, setLoadingId] = useState(null);
  const [currency, setCurrency] = useState(detectDefaultCurrency);
  const [paystackProduct, setPaystackProduct] = useState(null);
  const [showSampler, setShowSampler] = useState(false);
  const [showWaitlist, setShowWaitlist] = useState(false);

  useEffect(() => {
    client.get("/products").then(({ data }) => setProducts(data.products)).catch((err) => {
      logger.error("Failed to load products:", err);
    });
  }, []);

  const openStripe = async (pid) => {
    setLoadingId(pid);
    try {
      const { data } = await client.post("/payments/checkout", {
        product_id: pid,
        origin_url: window.location.origin,
        email: email || undefined,
      });
      window.location.href = data.checkout_url;
    } catch (err) {
      logger.error("Stripe checkout failed:", err);
      toast.error("Checkout failed. Please try again.");
    }
    setLoadingId(null);
  };

  const list = ["sampler", "cookbook", "bundle"];

  return (
    <div className="bg-mizizi-cream">
      <header className="max-w-4xl mx-auto px-6 md:px-10 pt-24 md:pt-40 pb-16 text-center">
        <div className="overline text-mizizi-terracotta">The Editions</div>
        <h1 className="font-serif font-light text-5xl md:text-7xl mt-6 leading-none">Choose your seat.</h1>
        <p className="mt-8 text-lg text-mizizi-charcoal/70">
          Start with the free sampler, or take the full seat at the Mizizi table.
        </p>

        <div
          role="tablist"
          aria-label="Currency"
          data-testid="currency-toggle"
          className="mt-10 inline-flex border border-mizizi-gold/40 bg-mizizi-cream"
        >
          {["USD", "KES"].map((c) => (
            <button
              key={c}
              type="button"
              role="tab"
              aria-selected={currency === c}
              onClick={() => setCurrency(c)}
              data-testid={`currency-${c.toLowerCase()}`}
              className={`overline px-5 py-2 transition-colors ${
                currency === c
                  ? "bg-mizizi-forest text-mizizi-gold"
                  : "text-mizizi-charcoal/70 hover:text-mizizi-charcoal"
              }`}
            >
              {c === "USD" ? "USD · Card" : "KES · M-PESA + Card"}
            </button>
          ))}
        </div>

        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="your@email.com (optional)"
          data-testid="shop-email"
          className="mt-6 w-full max-w-md px-5 py-3 bg-transparent border border-mizizi-gold/50 text-mizizi-charcoal placeholder:text-mizizi-charcoal/40 focus:outline-none focus:border-mizizi-terracotta"
        />
      </header>

      <section className="max-w-7xl mx-auto px-6 md:px-10 grid md:grid-cols-3 gap-8 pb-24">
        {list.map((pid, i) => {
          const p = products[pid];
          if (!p) return null;
          const featured = pid === "cookbook";
          const isWaitlist = p.availability === "waitlist";
          const isFree = p.availability === "free";
          const productWithId = { id: pid, ...p };

          return (
            <div
              key={pid}
              data-testid={`product-${pid}`}
              className={`relative border ${featured ? "border-mizizi-gold bg-mizizi-forest text-mizizi-cream" : "border-mizizi-gold/30 bg-mizizi-cream text-mizizi-charcoal"}`}
            >
              <Ribbon featured={featured} testid={`ribbon-${pid}`}>{RIBBONS[pid]}</Ribbon>

              <div className="aspect-[4/3] overflow-hidden bg-mizizi-charcoal">
                {pid === "cookbook" ? (
                  <FlipThrough variant="full" />
                ) : (
                  <img
                    src={IMGS[pid]}
                    alt={`${p.name} — editorial cookbook photograph`}
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                )}
              </div>

              <div className="p-8">
                <div className={`overline ${featured ? "text-mizizi-gold" : "text-mizizi-terracotta"}`}>Edition {String(i + 1).padStart(2, "0")}</div>
                <h2 className="font-serif text-3xl md:text-4xl mt-3 leading-tight">{p.name}</h2>
                <p className={`mt-2 text-sm italic ${featured ? "text-mizizi-cream/70" : "text-mizizi-charcoal/60"}`}>
                  {TAGLINES[pid]}
                </p>

                {/* Price / status */}
                {isFree && (
                  <div className="mt-4">
                    <div className="font-serif text-4xl md:text-5xl text-mizizi-gold" data-testid="price-free">Free</div>
                  </div>
                )}
                {isWaitlist && (
                  <div className="mt-4">
                    <div className="overline text-mizizi-gold">Coming soon</div>
                    <div className="font-serif text-2xl md:text-3xl mt-1" data-testid="price-waitlist">
                      Join the waitlist
                    </div>
                    {currency === "KES" && p.regular_price_kes && (
                      <div className="mt-1 text-sm text-mizizi-cream/60">
                        Founding {fmtKes(p.price_kes)} · Regular {fmtKes(p.regular_price_kes)}
                      </div>
                    )}
                  </div>
                )}
                {!isFree && !isWaitlist && (
                  currency === "KES" ? <KesPrice product={p} /> : <UsdPrice product={p} />
                )}

                <p className={`mt-4 ${featured ? "text-mizizi-cream/70" : "text-mizizi-charcoal/70"} leading-relaxed`}>{p.description}</p>

                <ul className={`mt-6 space-y-2 text-sm ${featured ? "text-mizizi-cream/80" : "text-mizizi-charcoal/80"}`}>
                  {FEATURES[pid].map((f) => (
                    <li key={f} className="flex gap-3">
                      <span className={`${featured ? "text-mizizi-gold" : "text-mizizi-terracotta"} mt-1`}>✦</span>
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>

                {/* CTA(s) */}
                <div className="mt-8 space-y-3">
                  {isFree && (
                    <>
                      <button
                        type="button"
                        onClick={() => setShowSampler(true)}
                        data-testid={`buy-${pid}`}
                        className={`w-full pill ${featured ? "pill-primary" : "pill-outline"} justify-center`}
                      >
                        Get the Free Sampler
                      </button>
                      <p className={`text-[11px] text-center ${featured ? "text-mizizi-cream/50" : "text-mizizi-charcoal/50"}`}>
                        Complimentary. Delivered directly to your inbox.
                      </p>
                    </>
                  )}

                  {!isFree && !isWaitlist && currency === "KES" && (
                    <>
                      <button
                        type="button"
                        onClick={() => setPaystackProduct(productWithId)}
                        data-testid={`buy-${pid}`}
                        className={`w-full pill ${featured ? "pill-primary" : "pill-outline"} justify-center`}
                      >
                        Pay with M-PESA or Card
                      </button>
                      <p className={`text-[11px] text-center ${featured ? "text-mizizi-cream/50" : "text-mizizi-charcoal/50"}`}>
                        Secured by Paystack · Kenyan Shillings
                      </p>
                    </>
                  )}

                  {!isFree && !isWaitlist && currency === "USD" && (
                    <button
                      type="button"
                      onClick={() => openStripe(pid)}
                      disabled={loadingId === pid}
                      data-testid={`buy-${pid}`}
                      className={`w-full pill ${featured ? "pill-primary" : "pill-outline"} justify-center disabled:opacity-50`}
                    >
                      {loadingId === pid ? "Redirecting…" : `Buy for ${fmtUsd(p.price)}`}
                    </button>
                  )}

                  {isWaitlist && (
                    <>
                      <button
                        type="button"
                        onClick={() => setShowWaitlist(true)}
                        data-testid={`buy-${pid}`}
                        className={`w-full pill ${featured ? "pill-primary" : "pill-outline"} justify-center`}
                      >
                        Join the Premium Waitlist
                      </button>
                      <p className={`text-[11px] text-center ${featured ? "text-mizizi-cream/50" : "text-mizizi-charcoal/50"}`}>
                        No charge until every element is ready.
                      </p>
                    </>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </section>

      <Divider />

      <section className="max-w-4xl mx-auto px-6 md:px-10 pb-24 text-center text-mizizi-charcoal/70">
        <div className="overline text-mizizi-terracotta">Secure checkout</div>
        <p className="mt-4">
          {currency === "KES"
            ? "M-PESA and Card payments securely processed by Paystack. Prices in Kenyan Shillings."
            : "Card payments processed by Stripe. Prices in USD."}
        </p>
      </section>

      <PaystackCheckoutModal
        open={!!paystackProduct}
        product={paystackProduct}
        prefEmail={email}
        onClose={() => setPaystackProduct(null)}
      />
      <SamplerCaptureModal
        open={showSampler}
        prefEmail={email}
        onClose={() => setShowSampler(false)}
      />
      <WaitlistModal
        open={showWaitlist}
        prefEmail={email}
        list="premium"
        onClose={() => setShowWaitlist(false)}
      />
    </div>
  );
}
